import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Percent, ListChecks } from "lucide-react";

type CommissionRule = {
  id: number;
  organizationId: number;
  name: string;
  scope: string;
  scopeEntityId: number | null;
  sourceFilter: string | null;
  beneficiaryType: string;
  beneficiaryId: number | null;
  commissionPct: string;
  commissionFixed: string;
  priority: number;
  isActive: boolean;
  createdAt: string | null;
};

type CommissionAllocation = {
  id: number;
  reservationId: number;
  beneficiaryType: string;
  beneficiaryId: number | null;
  amount: string;
  percentage: string;
  status: string;
  createdAt: string | null;
  reservationGuestName: string | null;
  beneficiaryName: string | null;
};

type RuleForm = {
  name: string;
  scope: string;
  scopeEntityId: string;
  sourceFilter: string;
  beneficiaryType: string;
  beneficiaryId: string;
  commissionPct: string;
  commissionFixed: string;
  priority: string;
  isActive: boolean;
};

const emptyRuleForm: RuleForm = {
  name: "",
  scope: "org",
  scopeEntityId: "",
  sourceFilter: "all",
  beneficiaryType: "admin",
  beneficiaryId: "",
  commissionPct: "0",
  commissionFixed: "0",
  priority: "0",
  isActive: true,
};

const SCOPES = [
  { value: "org", label: "Organization" },
  { value: "apartment", label: "Apartment" },
  { value: "owner", label: "Owner" },
  { value: "source", label: "Source" },
];

const SOURCE_FILTERS = [
  { value: "all", label: "All Sources" },
  { value: "airbnb", label: "Airbnb" },
  { value: "booking", label: "Booking.com" },
  { value: "direct_booking", label: "Direct Booking" },
  { value: "affiliate", label: "Affiliate" },
];

const BENEFICIARY_TYPES = [
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
  { value: "affiliate", label: "Affiliate" },
  { value: "platform", label: "Platform" },
];

function scopeLabel(scope: string) {
  return SCOPES.find((s) => s.value === scope)?.label || scope;
}

function beneficiaryLabel(type: string) {
  return BENEFICIARY_TYPES.find((b) => b.value === type)?.label || type;
}

function sourceLabel(source: string | null) {
  if (!source) return "All";
  return SOURCE_FILTERS.find((s) => s.value === source)?.label || source;
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "paid":
    case "completed":
      return "default";
    case "pending":
      return "secondary";
    case "cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

function RuleFormFields({ form, setForm }: { form: RuleForm; setForm: (f: RuleForm) => void }) {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <label className="text-sm font-medium">Rule Name</label>
        <Input
          placeholder="e.g. Default Admin Commission"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="input-rule-name"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Scope</label>
          <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
            <SelectTrigger data-testid="select-rule-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Scope Entity ID</label>
          <Input
            type="number"
            placeholder="Optional"
            value={form.scopeEntityId}
            onChange={(e) => setForm({ ...form, scopeEntityId: e.target.value })}
            data-testid="input-rule-scope-entity-id"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Source Filter</label>
        <Select value={form.sourceFilter} onValueChange={(v) => setForm({ ...form, sourceFilter: v })}>
          <SelectTrigger data-testid="select-rule-source-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_FILTERS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Beneficiary Type</label>
          <Select value={form.beneficiaryType} onValueChange={(v) => setForm({ ...form, beneficiaryType: v })}>
            <SelectTrigger data-testid="select-rule-beneficiary-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BENEFICIARY_TYPES.map((b) => (
                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Beneficiary ID</label>
          <Input
            type="number"
            placeholder="Optional"
            value={form.beneficiaryId}
            onChange={(e) => setForm({ ...form, beneficiaryId: e.target.value })}
            data-testid="input-rule-beneficiary-id"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Commission %</label>
          <Input
            type="number"
            step="0.01"
            placeholder="0"
            value={form.commissionPct}
            onChange={(e) => setForm({ ...form, commissionPct: e.target.value })}
            data-testid="input-rule-commission-pct"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Fixed Amount</label>
          <Input
            type="number"
            step="0.01"
            placeholder="0"
            value={form.commissionFixed}
            onChange={(e) => setForm({ ...form, commissionFixed: e.target.value })}
            data-testid="input-rule-commission-fixed"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Priority</label>
        <Input
          type="number"
          placeholder="0"
          value={form.priority}
          onChange={(e) => setForm({ ...form, priority: e.target.value })}
          data-testid="input-rule-priority"
        />
      </div>
      <div className="flex items-center gap-3">
        <Switch
          checked={form.isActive}
          onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
          data-testid="switch-rule-active"
        />
        <label className="text-sm font-medium">Active</label>
      </div>
    </div>
  );
}


export default function AdminCommissions() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("rules");
  const [createOpen, setCreateOpen] = useState(false);
  const [editRule, setEditRule] = useState<CommissionRule | null>(null);
  const [ruleForm, setRuleForm] = useState<RuleForm>(emptyRuleForm);

  const { data: rules, isLoading: rulesLoading } = useQuery<CommissionRule[]>({
    queryKey: ["/api/admin/commission-rules"],
  });

  const { data: allocations, isLoading: allocationsLoading } = useQuery<CommissionAllocation[]>({
    queryKey: ["/api/admin/commission-allocations"],
  });


  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/admin/commission-rules", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/commission-rules"] });
      setCreateOpen(false);
      setRuleForm(emptyRuleForm);
      toast({ title: "Commission rule created" });
    },
    onError: () => toast({ title: "Failed to create rule", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      apiRequest("PUT", `/api/admin/commission-rules/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/commission-rules"] });
      setEditRule(null);
      setRuleForm(emptyRuleForm);
      toast({ title: "Commission rule updated" });
    },
    onError: () => toast({ title: "Failed to update rule", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("DELETE", `/api/admin/commission-rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/commission-rules"] });
      toast({ title: "Commission rule deleted" });
    },
    onError: () => toast({ title: "Failed to delete rule", variant: "destructive" }),
  });

  const buildPayload = () => {
    const payload: Record<string, unknown> = {
      name: ruleForm.name,
      scope: ruleForm.scope,
      scopeEntityId: ruleForm.scopeEntityId ? Number(ruleForm.scopeEntityId) : null,
      sourceFilter: ruleForm.sourceFilter === "all" ? null : ruleForm.sourceFilter,
      beneficiaryType: ruleForm.beneficiaryType,
      beneficiaryId: ruleForm.beneficiaryId ? Number(ruleForm.beneficiaryId) : null,
      commissionPct: ruleForm.commissionPct || "0",
      commissionFixed: ruleForm.commissionFixed || "0",
      priority: Number(ruleForm.priority) || 0,
      isActive: ruleForm.isActive,
    };
    return payload;
  };

  const handleEdit = (rule: CommissionRule) => {
    setEditRule(rule);
    setRuleForm({
      name: rule.name,
      scope: rule.scope,
      scopeEntityId: rule.scopeEntityId ? String(rule.scopeEntityId) : "",
      sourceFilter: rule.sourceFilter || "all",
      beneficiaryType: rule.beneficiaryType,
      beneficiaryId: rule.beneficiaryId ? String(rule.beneficiaryId) : "",
      commissionPct: rule.commissionPct,
      commissionFixed: rule.commissionFixed,
      priority: String(rule.priority),
      isActive: rule.isActive,
    });
  };

  const sortedRules = rules ? [...rules].sort((a, b) => a.priority - b.priority) : [];

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Commission Rules
          </h1>
          <p className="text-muted-foreground mt-2">
            Define how commissions are split for reservations across owners, affiliates, and the platform.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="tabs-commissions">
          <TabsTrigger value="rules" data-testid="tab-rules">
            Rules
          </TabsTrigger>
          <TabsTrigger value="allocations" data-testid="tab-allocations">
            Allocations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => { setRuleForm(emptyRuleForm); setCreateOpen(true); }}
              data-testid="button-add-rule"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Rule
            </Button>
          </div>

          {rulesLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading rules...</div>
          )}

          {!rulesLoading && sortedRules.length === 0 && (
            <div className="text-center py-12" data-testid="text-empty-rules">
              <Percent className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No commission rules defined yet. Add your first rule to get started.</p>
            </div>
          )}

          <div className="space-y-3">
            {sortedRules.map((rule) => (
              <Card key={rule.id} data-testid={`card-rule-${rule.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium" data-testid={`text-rule-name-${rule.id}`}>
                          {rule.name}
                        </span>
                        <Badge variant={rule.isActive ? "default" : "secondary"} data-testid={`badge-rule-status-${rule.id}`}>
                          {rule.isActive ? "Active" : "Inactive"}
                        </Badge>
                        <Badge variant="outline" data-testid={`badge-rule-scope-${rule.id}`}>
                          {scopeLabel(rule.scope)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span data-testid={`text-rule-beneficiary-${rule.id}`}>
                          To: {beneficiaryLabel(rule.beneficiaryType)}
                          {rule.beneficiaryId ? ` #${rule.beneficiaryId}` : ""}
                        </span>
                        <span data-testid={`text-rule-commission-${rule.id}`}>
                          {parseFloat(rule.commissionPct) > 0 ? `${rule.commissionPct}%` : ""}
                          {parseFloat(rule.commissionPct) > 0 && parseFloat(rule.commissionFixed) > 0 ? " + " : ""}
                          {parseFloat(rule.commissionFixed) > 0 ? `$${rule.commissionFixed}` : ""}
                          {parseFloat(rule.commissionPct) === 0 && parseFloat(rule.commissionFixed) === 0 ? "0%" : ""}
                        </span>
                        <span data-testid={`text-rule-source-${rule.id}`}>
                          Source: {sourceLabel(rule.sourceFilter)}
                        </span>
                        <span data-testid={`text-rule-priority-${rule.id}`}>
                          Priority: {rule.priority}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(rule)}
                        data-testid={`button-edit-rule-${rule.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`button-delete-rule-${rule.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Commission Rule</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{rule.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="button-cancel-delete-rule">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(rule.id)}
                              data-testid="button-confirm-delete-rule"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Commission Rule</DialogTitle>
              </DialogHeader>
              <RuleFormFields form={ruleForm} setForm={setRuleForm} />
              <Button
                className="w-full"
                onClick={() => createMutation.mutate(buildPayload())}
                disabled={createMutation.isPending || !ruleForm.name.trim()}
                data-testid="button-submit-rule"
              >
                {createMutation.isPending ? "Creating..." : "Create Rule"}
              </Button>
            </DialogContent>
          </Dialog>

          <Dialog
            open={!!editRule}
            onOpenChange={(open) => { if (!open) { setEditRule(null); setRuleForm(emptyRuleForm); } }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Commission Rule</DialogTitle>
              </DialogHeader>
              <RuleFormFields form={ruleForm} setForm={setRuleForm} />
              <Button
                className="w-full"
                onClick={() => {
                  if (editRule) updateMutation.mutate({ id: editRule.id, data: buildPayload() });
                }}
                disabled={updateMutation.isPending || !ruleForm.name.trim()}
                data-testid="button-update-rule"
              >
                {updateMutation.isPending ? "Updating..." : "Update Rule"}
              </Button>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="allocations" className="mt-6">
          {allocationsLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading allocations...</div>
          )}

          {!allocationsLoading && (!allocations || allocations.length === 0) && (
            <div className="text-center py-12" data-testid="text-empty-allocations">
              <ListChecks className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No commission allocations yet.</p>
            </div>
          )}

          {allocations && allocations.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reservation</TableHead>
                      <TableHead>Beneficiary</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Percentage</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allocations.map((alloc) => (
                      <TableRow key={alloc.id} data-testid={`row-allocation-${alloc.id}`}>
                        <TableCell data-testid={`text-alloc-guest-${alloc.id}`}>
                          {alloc.reservationGuestName || `#${alloc.reservationId}`}
                        </TableCell>
                        <TableCell data-testid={`text-alloc-beneficiary-${alloc.id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{beneficiaryLabel(alloc.beneficiaryType)}</Badge>
                            <span className="text-sm text-muted-foreground">
                              {alloc.beneficiaryName || (alloc.beneficiaryId ? `#${alloc.beneficiaryId}` : "")}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell data-testid={`text-alloc-amount-${alloc.id}`}>
                          ${parseFloat(alloc.amount).toFixed(2)}
                        </TableCell>
                        <TableCell data-testid={`text-alloc-pct-${alloc.id}`}>
                          {alloc.percentage}%
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(alloc.status)} data-testid={`badge-alloc-status-${alloc.id}`}>
                            {alloc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm" data-testid={`text-alloc-date-${alloc.id}`}>
                          {alloc.createdAt ? new Date(alloc.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" }) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

      </Tabs>
    </AdminLayout>
  );
}
